import { useRef, useEffect } from "react";
import { Link } from "react-router";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { Retreat } from "@/data/content";
import ScriptText from "@/components/ui/ScriptText";

gsap.registerPlugin(ScrollTrigger);

interface RetreatBannerProps {
  retreat: Retreat;
  index: number;
}

export default function RetreatBanner({ retreat }: RetreatBannerProps) {
  const ref = useRef<HTMLDivElement>(null);

  const isUpcoming = retreat.status === "upcoming";

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReducedMotion) return;

    const textItems = el.querySelectorAll(".banner-reveal");
    const tl = gsap.fromTo(
      textItems,
      { opacity: 0, y: 24 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 75%",
          toggleActions: "play none none none",
        },
      },
    );

    return () => {
      tl.scrollTrigger?.kill();
    };
  }, []);

  const content = (
    <div
      ref={ref}
      className={`mx-auto max-w-2xl text-center ${isUpcoming ? "group" : "cursor-default"}`}
    >
      {/* Gold line + cartouche + gold line */}
      <div className="banner-reveal flex items-center justify-center gap-4">
        <span className="h-px w-16 bg-muted-gold/40 sm:w-24" />
        <p
          className={`text-xs tracking-[0.2em] uppercase ${
            isUpcoming ? "text-warm-stone" : "text-warm-stone/60"
          }`}
        >
          {retreat.season} &nbsp;·&nbsp; {retreat.location}
        </p>
        <span className="h-px w-16 bg-muted-gold/40 sm:w-24" />
      </div>

      {/* Title */}
      <h3
        className={`banner-reveal mt-6 font-serif text-2xl font-light md:text-3xl lg:text-4xl ${
          isUpcoming
            ? "text-soft-charcoal transition-colors duration-500 group-hover:text-faded-terracotta"
            : "text-soft-charcoal/50"
        }`}
      >
        {retreat.title}
      </h3>

      {/* Description */}
      <p
        className={`banner-reveal mt-5 font-body text-base leading-relaxed md:text-lg ${
          isUpcoming ? "text-soft-charcoal/75" : "text-soft-charcoal/50"
        }`}
      >
        {retreat.cardDescription}
      </p>

      {/* CTA or Coming Soon */}
      <div className="banner-reveal mt-6">
        {isUpcoming ? (
          <span className="inline-block font-body text-sm tracking-wide text-warm-stone transition-colors duration-300 group-hover:text-faded-terracotta">
            Explore this retreat &rarr;
          </span>
        ) : (
          <ScriptText className="block text-2xl">Coming Soon</ScriptText>
        )}
      </div>
    </div>
  );

  if (isUpcoming) {
    return (
      <Link to={`/retreats/${retreat.slug}`} className="block">
        {content}
      </Link>
    );
  }

  return content;
}
